import React, {useEffect, useMemo, useState} from 'react';
import {TOAST_SUCCESS, TOAST_FAIL, Toaster} from 'components/Toaster/Toaster';
import {toastOpen} from 'core/slices/toasts';
import './GridReportSlicers.scss';
import {Container} from 'react-bootstrap';
import {renderFilterComponent} from './functions/renderFilterComponent';
import {FileEarmarkExcel} from 'react-bootstrap-icons';
import {DropdownItem} from './DropDownButton/DropDownButton';
import {getMainAndOffcanvasFilters} from './functions/getMainAndOffcanvasFilters';
import {getFilterableColumns} from './functions/getFilterableColumns';
import {OffCanvasSidebar} from './components/offcanvas/OffCanvasSidebar';
import {ActionButtonsRow} from './components/action-buttons-row/ActionButtonsRow';
import {MainFiltersRow} from './components/main-filters-row/MainFiltersRow';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from 'core/store';
import {closeSidebar, openSidebar} from 'core/slices/sidebar';
import {FilterOptionValues} from 'features/gridReport/components/functions/loadData';
import {ColumnDefinition} from 'features/gridReport/models';
import {exportExcel} from './functions/exportExcel';
import {msalInstance} from 'index';
import {ExportingOverlay} from './components/ExportingOverlay';
import {ColumnSettingsModal} from "./components/offcanvas/ColumnSettingsModal";
import {toggleSelectedField} from "./components/offcanvas/functions/toggleSelectedField";

interface GridReportSlicersProps {
  filterValues: { key: string; value: string }[];
  filterOptionValues: FilterOptionValues;
  columns: ColumnDefinition[];
  onSaveColumnSelection?: (selectedFields: string[]) => void;
  onFilterChange?: (name: string, value: any) => void;
  onClearFilters?: () => void;
  countries: string[];
}

const minRequiredColumns = 7;

export const GridReportSlicers: React.FC<GridReportSlicersProps> = ({
                                                                      columns,
                                                                      filterValues,
                                                                      filterOptionValues,
                                                                      onSaveColumnSelection,
                                                                      onFilterChange,
                                                                      onClearFilters,
                                                                      countries,
                                                                    }) => {
  const [exporting, setExporting] = useState(false);
  const dispatch = useDispatch();
  const showSidebar = useSelector((state: RootState) => state.sidebar.isOpen);
  const handleClose = () => dispatch(closeSidebar());
  const handleShow = () => dispatch(openSidebar());
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const handleClearFilters = () => {
    onClearFilters && onClearFilters();
  };
  const handleShowColumnSettings = () => {
    setShowColumnSettings(true);
  }


  const handleOnExportExcel = async () => {
    try {
      setExporting(true);
      await exportExcel(msalInstance);
      dispatch(
        toastOpen({
          className: TOAST_SUCCESS,
          text: 'Excel export completed successfully',
        })
      );
    } catch (error) {
      dispatch(
        toastOpen({
          className: TOAST_FAIL,
          text:
            'Failed to export Excel file: ' +
            (error instanceof Error ? error.message : 'Unknown error'),
        })
      );
    } finally {
      setExporting(false);
    }
  };

  const hasFilterValues = filterValues && filterValues.length > 0;

  const filterableColumns = getFilterableColumns(columns);
  const {mainFilters, offCanvasFilters} =
    getMainAndOffcanvasFilters(filterableColumns);

  const exportItems: DropdownItem[] = [
    {
      label: 'Save as Excel',
      onClick: handleOnExportExcel,
      icon: <FileEarmarkExcel/>,
    },
  ];

  const columnOptions = useMemo(
    () =>
      columns.map(col => ({
        field: String(col.field),
        label: col.label || String(col.field),
        show: col.show !== false,
        filterable: col.filterable,
        systemHidden: col.systemHidden === true,
      })),
    [columns]
  );

  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  const toggleField = (field: string) => {
    setSelectedFields(prev =>
      toggleSelectedField(prev, field, {
        requiredFields,
        minRequired: minRequiredColumns,
      })
    );
  };


  const requiredFields = useMemo(
    () =>
      columnOptions
        .filter(col => col.filterable && !col.systemHidden)
        .map(col => col.field),
    [columnOptions]
  );

  /**
   * This effect recalculates the currently visible fields (col.show) whenever columnOptions changes and updates local state, so the modal always reflects the latest column configuration.
   */
  useEffect(() => {
    const initial = columnOptions.filter(col => col.show).map(col => col.field);
    setSelectedFields(initial);
  }, [columnOptions]);

  const handleSave = () => {
    const selectedRequiredCount = selectedFields.filter(field =>
      requiredFields.includes(field)
    ).length;
    if (selectedRequiredCount < minRequiredColumns) {
      return;
    }
    onSaveColumnSelection?.(selectedFields);
    setShowColumnSettings(false);
  };

  const handleCancel = () => {
    const reset = columnOptions.filter(col => col.show).map(col => col.field);
    setSelectedFields(reset);
    setShowColumnSettings(false);
  };

  if (mainFilters.length === 0) return null;

  return (
    <div id="grid-report-slicers">
      {exporting && <ExportingOverlay message="Exporting Excel file..."/>}
      <Container fluid className="filter-container sticky">
        <MainFiltersRow
          mainFilters={mainFilters}
          renderFilterComponent={item =>
            renderFilterComponent(
              item,
              filterValues,
              filterOptionValues,
              countries,
              onFilterChange
            )
          }
        />
        <ActionButtonsRow
          hasFilterValues={hasFilterValues}
          handleClearFilters={handleClearFilters}
          exportItems={exportItems}
          offCanvasFiltersLength={offCanvasFilters.length}
          handleShow={handleShow}
          handleShowColumnSettings={handleShowColumnSettings}
        />
      </Container>

      <OffCanvasSidebar
        show={showSidebar}
        handleClose={handleClose}
        offCanvasFilters={offCanvasFilters}
        columns={columns}
        onSaveColumnSelection={onSaveColumnSelection}
        renderFilterComponent={item =>
          renderFilterComponent(
            item,
            filterValues,
            filterOptionValues,
            countries,
            onFilterChange
          )
        }
      />
      <ColumnSettingsModal
        show={showColumnSettings}
        columnOptions={columnOptions}
        selectedFields={selectedFields}
        requiredFields={requiredFields}
        minRequired={minRequiredColumns}
        onToggleField={toggleField}
        onCancel={handleCancel}
        onSave={handleSave}
      />
      <Toaster/>
    </div>
  );
};
